import {PlusOutlined} from '@ant-design/icons';
import {activityPlaces} from '@mep/pae-commons';
import {Button, Form, message} from 'antd';
import FormBuilder from 'antd-form-builder';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import React, {memo, useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {useMutation, useQueryClient} from 'react-query';
import useDeepCompareEffect from 'use-deep-compare-effect';

import {EntityDetailContent} from '@/components/EntityDetailContent';
import FloatContainer, {TYPE_CONTAINER} from '@/components/FloatContainer';
import FormTitleSession from '@/components/FormTitleSession';
import {useDialogsContext} from '@/contexts/DialogsContext';
import useAlerts from '@/hooks/useAlerts';
import {
  CACHE_KEY_LIST_ACTIVITY_PLACE,
  DIALOG_ACTIVITY_PLACE_NAMESPACE
} from '@/modules/activityPlace/contants/activityPlace';
import {useFieldsForProposal} from '@/modules/activityPlace/hooks/useActivityPlace';
import ActivityPlaceApiService from '@/modules/activityPlace/services/ActivityPlaceApiService';
import OfficeList from '@/modules/company/containers/CompanyInfoDetailContainer/containers/OfficesPanelContainer';

const {ACTIVITY_PLACE_TYPES} = activityPlaces;

const ActivityPlaceProposalDialogForm = ({proposalId, procedure, isTCPProposal, proposal}) => {
  const {t} = useTranslation('proposalRequest');
  const queryClient = useQueryClient();
  const {putErrorCode} = useAlerts();
  const {isOpen, closeDialog} = useDialogsContext(DIALOG_ACTIVITY_PLACE_NAMESPACE);
  const [form] = Form.useForm();
  const forceUpdate = FormBuilder.useForceUpdate();
  
  const initValues = useMemo(() => ({
    proposal: proposalId,
    procedure: procedure?._id || procedure?.id || procedure,
    type: isTCPProposal ? ACTIVITY_PLACE_TYPES.AMBULATORY : ACTIVITY_PLACE_TYPES.STANDARD,
    isOnPeninsula9045: false
  }), [proposalId, procedure, isTCPProposal]);
  
  const fields = useFieldsForProposal(false, form, 2, isTCPProposal, proposal);
  
  const {mutate, isLoading} = useMutation(ActivityPlaceApiService?.save, {
    onSuccess: ({data}) => {
      message.success(t('updateSuccess'));
      queryClient.invalidateQueries([CACHE_KEY_LIST_ACTIVITY_PLACE, proposalId, procedure]);
      queryClient.invalidateQueries(CACHE_KEY_LIST_ACTIVITY_PLACE);
      queryClient.invalidateQueries(data?._id);
    },
    onError: (e) => {
      putErrorCode(e);
    }
  });
  
  const close = useCallback(() => {
    closeDialog();
    setTimeout(() => {
      form.resetFields();
    }, 500);
  }, [closeDialog, form]);
  
  const onFinish = useCallback(async (values) => {
    await mutate({...values, proposal: proposalId, procedure, status: isTCPProposal ? 'ACCEPTED' : 'PENDING'});
    close();
  }, [close, isTCPProposal, mutate, procedure, proposalId]);
  
  const handleSubmit = useCallback(() => {
    form.validateFields().then(() => {
      const data = form.getFieldsValue();
      onFinish(data).catch(e => console.log(e));
    }).catch(({errorFields}) => {
      form.scrollToField(errorFields && errorFields[0]?.name && errorFields[0]?.name[0]);
    });
  }, [form, onFinish]);
  
  const onCLose = useCallback(() => {
    closeDialog();
    form.resetFields();
  }, [closeDialog, form]);
  
  useDeepCompareEffect(() => {
    if (!isEmpty(initValues)) {
      form.setFieldsValue(initValues);
    }
  }, [initValues]);
  
  return <FloatContainer
    type={TYPE_CONTAINER.MODAL}
    visible={isOpen}
    title={t('proposalRequest.form.activityPlace.addText')}
    closable={false}
    bodyStyle={{padding: '0.75rem 1.5rem 0.1rem 1.5rem '}}
    onClose={onCLose}
    width={700}
    footer={[
      <Button loading={isLoading} className="ml-2" key="back" onClick={onCLose}>{t('cancel')}</Button>,
      <Button loading={isLoading} className="ml-2" key="submit" type="primary" onClick={() => form.submit()}>
        {t('confirm')}
      </Button>,
    ]}>
    <Form form={form} onFinish={handleSubmit} layout="vertical" onValuesChange={forceUpdate} scrollToFirstError
      initialValues={initValues}>
      <EntityDetailContent viewMode={false} form={form} data={initValues} fields={fields} columns={2}/>
    </Form>
  </FloatContainer>;
};

ActivityPlaceProposalDialogForm.propTypes = {
  proposalId: PropTypes.string,
  isTCPProposal: PropTypes.bool,
  procedure: PropTypes.string,
  proposal: PropTypes.object,
};

const ActivityPlacesSection = ({canEdit, proposal, procedure, helpText, title, isTCPProposal, ...rest}) => {
  const {t} = useTranslation('proposalRequest');
  const {openDialog} = useDialogsContext(DIALOG_ACTIVITY_PLACE_NAMESPACE);
  
  return (
    <div>
      <ActivityPlaceProposalDialogForm
        proposalId={proposal?._id}
        proposal={proposal}
        procedure={procedure}
        isTCPProposal={isTCPProposal}
      />
      <FormTitleSession
        translate={'proposalRequest'}
        title={title || 'proposalRequest.form.activityPlace.title'}
        description={helpText || 'proposalRequest.form.activityPlace.help'}
        divider
      />
      <div className='mb-4'>
        <OfficeList
          isTCPProposal={isTCPProposal}
          proposalId={proposal?._id}
          procedureId={procedure?._id}
          className={''}
          allowRemove
          {...rest}
        />
      </div>
      {!canEdit ? null :
        <Button
          type={'primary'}
          onClick={openDialog}
          className={'cursor-pointer mb-2 bg-secondary border-secondary'}
          size={'large'} icon={<PlusOutlined/>}
        >
          {t('proposalRequest.form.activityPlace.addText')}
        </Button>}
    
    </div>
  );
};

ActivityPlacesSection.propTypes = {
  procedure: PropTypes.string,
  title: PropTypes.string,
  proposal: PropTypes.object,
  canEdit: PropTypes.bool,
  isTCPProposal: PropTypes.bool,
  softDelete: PropTypes.bool,
  helpText: PropTypes.string,
};

ActivityPlacesSection.defaultProps = {
  canEdit: true
};

export default memo(ActivityPlacesSection);
